import type { ReactNode } from "react";
import { Box, Sheet, type Theme } from "@mui/joy";
import { useIsAppBusy } from "../shared/appBusy";
import { SIDEBAR_RAIL_LIST_WIDTH } from "../shell/railMetrics";
import LogoDark from "./LogoDark";
import PrimepointMark, { useMarkLoading } from "./PrimepointMark";

const styles = {
    headerWrapper: (theme: Theme) => ({
        flex: "0 0 auto",
        position: "sticky" as const,
        top: 0,
        pt: `calc(env(safe-area-inset-top) + ${theme.spacing(1)})`,
        pb: 1,
        px: { xs: 1.5, sm: 3 },
        zIndex: 999,
        boxShadow: theme.vars.shadow.md,
    }),
    // The v2 app bar casts no shadow (DESIGN.md · Chrome).
    workspaceHeader: {
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        minHeight: 56,
        // The mark sits in a slot as wide as the rail under it, from the bar's edge.
        pl: 0,
        pr: { xs: 1.5, sm: 3 },
        border: 0,
        bgcolor: "background.chrome",
        zIndex: 999,
    },
};

/** The header geometry is available before project or authentication data loads. */
export function HeaderFrame({
    navigation,
    children,
}: {
    navigation: "horizontal" | "rail";
    children: ReactNode;
}) {
    return (
        <Sheet
            component="header"
            aria-label="Primepoint"
            data-web-header={navigation}
            sx={navigation === "rail" ? styles.workspaceHeader : styles.headerWrapper}
            color={navigation === "rail" ? "neutral" : "primary"}
            variant={navigation === "rail" ? "plain" : "solid"}
            invertedColors={navigation === "horizontal"}
        >
            {children}
        </Sheet>
    );
}

const markTileSx = {
    display: "flex",
    p: 0.75,
    borderRadius: "lg",
    transition: "background-color 120ms",
};

/**
 * Design v2 (`rail`): Primepoint's mark, centred over the rail and animated while `loading`, which
 * names the wait for screen readers ("Opening" while a project is still opening). The classic bar
 * keeps the wordmark.
 */
export function HeaderBrand({
    navigation,
    loading,
}: {
    navigation: "horizontal" | "rail";
    loading?: string;
}) {
    if (navigation === "rail") {
        return (
            <Box
                sx={{
                    width: SIDEBAR_RAIL_LIST_WIDTH,
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                {/* The tile the bar's Home link lights on hover and focus (Header). */}
                <Box data-mark-tile sx={markTileSx}>
                    <PrimepointMark loading={loading !== undefined} label={loading} />
                </Box>
            </Box>
        );
    }
    return (
        <Box sx={{ display: "flex", color: "inherit" }}>
            <Box sx={{ display: { xs: "none", lg: "flex" } }}>
                <LogoDark color="white" />
            </Box>
            <Box sx={{ display: { xs: "flex", lg: "none" } }}>
                <LogoDark markOnly color="white" />
            </Box>
        </Box>
    );
}

/** The app bar's brand: the mark runs while anything on screen is waiting (`shared/appBusy`). Its
 *  own component, so a wait re-renders the mark and not the whole header. */
export function AppBarBrand({ navigation }: { navigation: "horizontal" | "rail" }) {
    const loading = useMarkLoading(useIsAppBusy());
    return <HeaderBrand navigation={navigation} loading={loading ? "Working" : undefined} />;
}
